import { Paper, Typography } from "@mui/material";
import { CustomGridColDef, DataGrid } from "components/DataGrid";
import { useMemo } from "react";

export interface RateRow {
  key: string;
  name: string;
  decided: number;
  substantiveRejections: number;
  ratePct: number | null;
  facilities?: number;
  /** The CUPA's raw code. Present on CUPA rows; absent on other row types. */
  cupaCode?: string;
}

type TableVariant = "rate" | "category";

const formatPct = (value: number | null | undefined) => (value == null ? "—" : `${value}%`);

// Rows arrive pre-sorted from the API (ratePct desc, name asc) as the initial
// order; the grid's client-side column sorting can re-order from there.
export function RateTable({
  title,
  nameHeader,
  rows,
  loading,
  showFacilities = false,
  variant = "rate",
  getHref,
}: {
  title: string;
  nameHeader: string;
  rows: RateRow[];
  loading: boolean;
  showFacilities?: boolean;
  variant?: TableVariant;
  getHref?: (row: RateRow) => string | null;
}) {
  const columns: CustomGridColDef<RateRow>[] = useMemo(() => {
    const nameColumn: CustomGridColDef<RateRow> = {
      field: "name",
      headerName: nameHeader,
      flex: 1,
    };
    if (variant === "category") {
      return [
        nameColumn,
        { field: "decided", headerName: "Count", type: "number", width: 90 },
        {
          field: "ratePct",
          headerName: "% of Substantive",
          type: "number",
          width: 150,
          valueFormatter: formatPct,
        },
      ];
    }
    const facilitiesColumns: CustomGridColDef<RateRow>[] = showFacilities
      ? [{ field: "facilities", headerName: "Facilities", type: "number", width: 100 }]
      : [];
    return [
      nameColumn,
      ...facilitiesColumns,
      { field: "decided", headerName: "Decided", type: "number", width: 100 },
      {
        field: "substantiveRejections",
        headerName: "Substantive Rejections",
        type: "number",
        width: 180,
      },
      {
        field: "ratePct",
        headerName: "Rate %",
        type: "number",
        width: 90,
        valueFormatter: formatPct,
      },
    ];
  }, [nameHeader, variant, showFacilities]);

  return (
    <Paper elevation={0} sx={{ p: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        {title}
      </Typography>
      <DataGrid
        autoHeight
        rows={rows}
        columns={columns}
        getRowId={(row: RateRow) => row.key}
        loading={loading}
        noRowsMessage="No CERS decisions in the last 12 months."
        getRowLink={getHref}
        pagination
        initialState={{ pagination: { paginationModel: { pageSize: 25, page: 0 } } }}
        pageSizeOptions={[25, 50, 100]}
        disableColumnMenu
        density="compact"
      />
    </Paper>
  );
}
